import { gql } from "@apollo/client";
import { faArrowDownUpAcrossLine, faBolt, faCalendarDay, faHashtag, faPlus, faRepeat, faTerminal } from "@fortawesome/free-solid-svg-icons";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import Page, { PageActionsType } from "components/common/page/Page";
import { uuid } from "config/app-constants";
import { graph } from "config/axios-context";
import { AppRoutes } from "config/router";
import { EventTemplateDatalistPageQuery, EventTemplateDatalistPageQueryVariables, EventTemplateFilterInput, EventTemplateSortInput, SortEnumType } from "types/graphql";
import { Roles } from "utilities/app-utilities";
import { appendFilters } from "utilities/datalist/datalist-filter-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import EventTemplateOnDayView from "./partials/EventTemplateOnDayView";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DatalistRowActions from "components/common/datalist/partials/DatalistRowActions";
import BooleanView from "components/common/views/BooleanView";
import { deleteMutation } from "utilities/entities/common/delete-mutation";
import { DatalistColumnDefinitions } from "@/utilities/datalist/datalist-utilities";
const query = gql`
query EventTemplateDatalistPage($where: EventTemplateFilterInput, $order: [EventTemplateSortInput!], $skip: Int, $take: Int) {
    eventTemplates {
      items(order: $order, skip: $skip, take: $take, where: $where) {
        id
        name
        onDay
        repeat
        isSystem
        isActive
        changeDynamicly
        order,
        assigneeId,
        assignee {
          id
          name
        }
      }
    }
  }
`;

type DataItem = NonNullable<EventTemplateDatalistPageQuery['eventTemplates']>['items'][0];

const EventTemplateDatalistPage = () => {
    const context = useAppContext();
    const pageActions: PageActionsType = [
        {
            title: 'common.add',
            icon: faPlus,
            link: AppRoutes.ACCOMMODATION_EVENT_TEMPLATE_FORM,
            name: 'add',
            hide: !context.userHasRole(Roles.ADMIN)
        }
    ]

    const datalistActions = (i: DataItem) => ({
        mutationPermissionRole: [Roles.ADMIN],
        delete: i.isSystem ? undefined : async (item: DataItem) => {
            await deleteMutation('eventTemplateDelete', item.id);
        },
        editUrl: AppRoutes.ACCOMMODATION_EVENT_TEMPLATE_EDIT
    });

    const datalistConfig: DatalistPropsType<DataItem, EventTemplateFilterInput, EventTemplateSortInput> = {
        dataSource: {
            request: async params => {
                let variables: EventTemplateDatalistPageQueryVariables = {
                    where: params.filters,
                    order: params.sort,
                    skip: params.skip,
                    take: params.take
                };

                if (params.searchText) {
                    variables.where = appendFilters(variables.where, {
                        name: {
                            contains: params.searchText
                        }
                    });
                }

                try {
                    const response = await graph<EventTemplateDatalistPageQuery, EventTemplateDatalistPageQueryVariables>({ query, variables });
                    if (response.errors) {
                        throw response.errors;
                    }

                    return response.data!.eventTemplates!;
                } catch (error: any) {
                    context.notify('error', 'context.load.error', Array.isArray(error) ? error[0].message : error.message);
                    throw error;
                }
            },
            sort: [
                {
                    name: 'name',
                    direction: SortEnumType.Asc,
                    id: uuid()
                }
            ]
        },
        itemId: i => i.id,
        name: 'eventTemplateDatalist',
        table: {
            columns: [
                {
                    name: 'id',
                },
                {
                    name: 'name',
                    sort: true,
                    filter: {
                        type: 'string'
                    },
                },
                {
                    ...DatalistColumnDefinitions.personColumnDefinitionType(context, ["assignee", 'name'], {
                        name: 'assignee',
                        title: 'fields.assignee.name',
                    })
                },
                {
                    name: 'onDay',
                    type: 'number',
                    sort: true,
                    filter: {
                        type: 'number'
                    },
                    resolve: i => <EventTemplateOnDayView onDay={i.onDay!} repeat={i.repeat} />,
                },
                {
                    name: 'repeat',
                    type: 'boolean',
                    resolve(i) {
                        const dynamic = i.changeDynamicly ? <FontAwesomeIcon icon={faArrowDownUpAcrossLine} className="text-success" /> : <></>;
                        return <span>
                            <BooleanView value={i.repeat} />
                            {dynamic}
                        </span>
                    },
                },
                {
                    name: 'isActive',
                    type: 'boolean',
                },
                {
                    name: 'isSystem',
                    type: 'boolean',
                },
                { // order
                    name: 'order',
                    type: "number",
                    sort: true,
                    filter: {
                        type: 'number'
                    }
                }
            ],
            render: {
                card: i => <div className="datalist-card ">
                    <div className="grid grid-cols-2">
                        <div className="grid gap-2">
                            <div>
                                <span><FontAwesomeIcon icon={faHashtag} /> {i.name}</span>
                            </div>
                            <div>
                                <span><FontAwesomeIcon icon={faCalendarDay} /> <EventTemplateOnDayView onDay={i.onDay!} repeat={i.repeat} /></span>
                            </div>
                        </div>
                        <div>
                            <div>
                                <span><FontAwesomeIcon icon={faRepeat} className={i.repeat ? 'text-success' : 'text-danger'} /></span>
                            </div>
                            <div>
                                <span><FontAwesomeIcon icon={faBolt} className={i.isActive ? 'text-success' : 'text-danger'} /></span>
                            </div>
                            <div>
                                <span><FontAwesomeIcon icon={faTerminal} className={i.isSystem ? 'text-success' : 'text-danger'} /></span>
                            </div>
                        </div>
                    </div>
                    <div className="pt-5">
                        <DatalistRowActions
                            {...datalistActions(i)}
                            itemId={i.id}
                            item={i}
                            name="eventTemplateDatalist-card-actions"
                            onDelete={() => { }}
                            view='cart'
                        />
                    </div>
                </div>,
                actions: datalistActions
            }
        }
    }

    return (
        <Page wFull title="pages.accommodation.eventTemplate.list" actions={pageActions}>
            <Datalist {...datalistConfig} />
        </Page>
    );
};

export default EventTemplateDatalistPage;
